import { AxiosInstance } from 'axios';
import { objectName } from '../pages/issueModel';
import { duration } from './format';

/**
 * What passed through the connector of one environment lately (GET /Cockpit/{c}/{e}/passing), and the chips the
 * live flow moves along its wires: one per object, at the station it reached. Mirrors CockpitPassingDto.
 */

export type PassingDirection = 'ToExact' | 'ToChargebee';
export type PassingStage = 'Received' | 'Handled' | 'Failed';

export interface PassingItem {
    key: string;
    at: string;
    direction: PassingDirection;
    stage: PassingStage;
    outcome?: string | null;
    objectType: string;
    eventType?: string | null;
    relationId?: string | null;
    problem?: string | null;
}

export interface Passing {
    readAt: string;
    items: PassingItem[];
}

const body = <T>(data: unknown): T => (typeof data === 'string' && data.length > 0 ? JSON.parse(data) : data) as T;

export const getPassing = async (axios: AxiosInstance, customer: string, environment: string, since?: string, signal?: AbortSignal): Promise<Passing> =>
    body<Passing>(
        (await axios.get(`/Cockpit/${encodeURIComponent(customer)}/${encodeURIComponent(environment)}/passing`, { params: since ? { since } : undefined, signal })).data
    );

/** Where on its wire a chip stands: where it came from, in the connector, or where it went. */
export type Station = 'from' | 'connector' | 'to';
export type ChipTone = 'move' | 'ok' | 'off' | 'bad';

export interface Chip {
    key: string;
    direction: PassingDirection;
    /** "factuur 2026-00409": what people call it. */
    label: string;
    /** The full id, for the tooltip. */
    title: string;
    station: Station;
    tone: ChipTone;
    /** "Ontvangen", "In Exact · 7s", "Mislukt". */
    status: string;
    /** When it was first seen, in ms; when it reached its present station. */
    firstAt: number;
    lastAt: number;
    /** Nothing more will happen to it on this wire. */
    done: boolean;
}

/** How long a chip that is done stays, and when it starts to fade. */
export const DONE_FOR = 15000;
export const FADE_AFTER = 11000;
/** How long a failure stays; how long an object waits in the connector before the queue counts take over. */
export const FAILED_FOR = 60000;
export const WAITING_FOR = 180000;
/** The most chips per station on one wire; the rest are a count. */
export const PER_STATION = 3;

const nounOf = (objectType: string) => (objectType === 'Event' ? 'Exact-melding' : objectType === 'EventBankEntry' ? 'bankregel' : objectName(objectType) || objectType);

const shortId = (id: string) => (id.length > 16 ? `${id.slice(0, 15)}…` : id);

const doneStatus = (item: PassingItem): { station: Station; tone: ChipTone; status: string } => {
    const target = item.direction === 'ToExact' ? 'In Exact' : 'In Chargebee';
    switch (item.outcome) {
        case 'AlreadySynced':
            return { station: 'to', tone: 'ok', status: 'Stond er al' };
        case 'Unhandled':
            return { station: 'connector', tone: 'off', status: 'Overgeslagen' };
        default:
            return { station: 'to', tone: 'ok', status: target };
    }
};

/**
 * The chips after a reading: each moment moves its object's chip to the station it reached. A moment older than what
 * the chip already shows changes nothing; a new attempt after a failure moves it on again.
 */
export const applyPassing = (chips: Map<string, Chip>, items: PassingItem[]): Map<string, Chip> => {
    const next = new Map(chips);
    [...items]
        .sort((a, b) => Date.parse(a.at) - Date.parse(b.at))
        .forEach((item) => {
            const at = Date.parse(item.at);
            const known = next.get(item.key);
            if (known && at < known.lastAt) return;
            const id = item.relationId ?? '';
            const noun = nounOf(item.objectType);
            const base = {
                key: item.key,
                direction: item.direction,
                label: id ? `${noun} ${shortId(id)}` : noun,
                title: [noun, id, item.eventType, item.problem].filter(Boolean).join(' · '),
                firstAt: known?.firstAt ?? at,
                lastAt: at,
            };
            if (item.stage === 'Received') {
                next.set(item.key, { ...base, station: 'connector', tone: 'move', status: 'Ontvangen', done: false });
                return;
            }
            if (item.stage === 'Failed') {
                next.set(item.key, { ...base, station: 'connector', tone: 'bad', status: 'Mislukt', done: false });
                return;
            }
            const end = doneStatus(item);
            // How long it took, when the chip saw it come in.
            const took = known && known.firstAt < at ? ` · ${duration(Math.round((at - known.firstAt) / 1000))}` : '';
            next.set(item.key, { ...base, ...end, status: end.status + (end.tone === 'ok' ? took : ''), done: true });
        });
    return next;
};

/** Whether a chip is still shown at `now`. */
export const stillShown = (chip: Chip, now: number) =>
    chip.done ? now - chip.lastAt < DONE_FOR : chip.tone === 'bad' ? now - chip.lastAt < FAILED_FOR : now - chip.lastAt < WAITING_FOR;

/** The chips gone from the screen taken out. */
export const prune = (chips: Map<string, Chip>, now: number): Map<string, Chip> => {
    const next = new Map<string, Chip>();
    chips.forEach((chip, key) => stillShown(chip, now) && next.set(key, chip));
    return next;
};

/** Per wire and station the newest chips, at most PER_STATION, with how many more stand there. */
export const layout = (chips: Map<string, Chip>, direction: PassingDirection) => {
    const placed: { chip: Chip; slot: number }[] = [];
    const more: Partial<Record<Station, number>> = {};
    (['from', 'connector', 'to'] as Station[]).forEach((station) => {
        const here = Array.from(chips.values())
            .filter((c) => c.direction === direction && c.station === station)
            .sort((a, b) => b.lastAt - a.lastAt);
        here.slice(0, PER_STATION).forEach((chip, slot) => placed.push({ chip, slot }));
        if (here.length > PER_STATION) more[station] = here.length - PER_STATION;
    });
    return { placed, more };
};

/** Whether something passed on a wire in the last `withinMs`: what makes its dots move. */
export const recentlyPassed = (chips: Map<string, Chip>, direction: PassingDirection, now: number, withinMs = 30000) =>
    Array.from(chips.values()).some((c) => c.direction === direction && now - c.lastAt < withinMs);
